/**
 * useMediaQuery Hook
 *
 * CSS 미디어 쿼리의 일치 여부를 구독한다.
 *
 * @why 화면 폭에 따라 "표현을 통째로 바꿔야" 할 때 쓴다.
 * CSS의 `hidden`으로 양쪽을 다 그려 두고 감추는 방법도 있지만,
 * 그러면 같은 입력 칸이 DOM에 두 벌 존재해 보조 기술이 중복해서 읽고
 * 폼 요소도 두 배로 생긴다. 실제로 하나만 렌더하기 위해 JS로 판별한다.
 */

import { useEffect, useState } from 'react'

/**
 * @param query 미디어 쿼리 문자열 (예: '(min-width: 768px)')
 * @returns 현재 화면이 조건에 맞는지 여부
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // matchMedia가 없는 환경(SSR 등)에서도 안전하게 동작해야 한다
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }

    const mediaQueryList = window.matchMedia(query)

    // 훅이 붙기 전에 화면이 이미 바뀌었을 수 있으므로 현재 값으로 한 번 맞춘다
    const sync = () => setMatches(mediaQueryList.matches)
    sync()

    mediaQueryList.addEventListener('change', sync)

    /**
     * resize 폴백
     *
     * @why change 이벤트가 오지 않은 채 창 크기만 바뀌는 환경을 실제로 확인했다
     * (matchMedia(...).matches는 이미 true인데 화면은 좁은 화면용 표현으로 굳어 있었다).
     * change에만 의존하면 그 순간 잘못된 표현이 그대로 남는다.
     * 두 경로 모두 같은 값을 다시 읽을 뿐이라 중복 실행돼도 문제가 없다.
     */
    window.addEventListener('resize', sync)

    return () => {
      mediaQueryList.removeEventListener('change', sync)
      window.removeEventListener('resize', sync)
    }
  }, [query])

  return matches
}
